<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import { Table, TableIconText } from '@/components/common/';
import { TableColumnType } from '@/components/common/table/interfaces/TableColumnModel';
import PipelineCell from './PipelineCell.vue';
import { useRoute, useRouter } from 'vue-router';
import { CaseModel } from '@/models';
import { ENTRY_DIALOG_QUERY, routeConstants } from '@/utils';
import { useEntryListStore } from '@/stores/entryList';
import { usePipelineStore } from '@/stores/pipeline';
import { useCaseStore } from '@/stores/case';
import { useStudyStore } from '@/stores/study';
import CaseNoteButton from './CaseNoteButton.vue';
import PerfusionScanButton from './PerfusionScanButton.vue';

const props = defineProps({
  selectedCases: {
    type: Object as PropType<CaseModel[]>,
    required: true
  },
  emptyContentMessage: {
    type: String
  }
});

const emit = defineEmits(['update:selectedCases', 'onNotesPinChange']);

const selectedRows = computed({
  get: () => props.selectedCases,
  set: value => {
    emit('update:selectedCases', value);
  }
});

const entryStore = useEntryListStore();
const pipelineStore = usePipelineStore();
const caseStore = useCaseStore();
const studyStore = useStudyStore();
const showTable = ref(false);

const canShowCaseNotes = computed(() => {
  const flags = studyStore.selectedStudy?.flags as
    | Record<string, unknown>
    | undefined;
  return flags?.['enableCaseNotes'] === true;
});

/** Waiting for base component to load before loading the table, 
  * as table is resource heavy and takes time to load causing UI to freez */
setTimeout(() => {
  showTable.value = true;
  pipelineStore.getPipelineCases();
}, 100);

const router = useRouter();
const route = useRoute();
const onCaseClick = (caseModel: CaseModel) => {
  entryStore.selectedCase = caseModel;
  caseStore.caseList = pipelineStore.caseList;
  pipelineStore.caseList = [];
  router.push({
    name: routeConstants.CASE.name,
    params: {
      identifier: caseModel.identifier
    }
  });
};

/**
 * Opens the entry as a dialog over the pipeline instead of navigating to the entry page, so the grid keeps its scroll
 * position, selection and page. The identifiers go into the query rather than the route so the pipeline route — and
 * therefore this table — stays mounted, while the URL still supports deep-linking, refresh and the back button.
 */
const handleCellClick = (caseItem: CaseModel, formIdentifier: string, entryIdentifier?: string) => {
  if (!entryIdentifier && caseItem.lockedAt) {
    const errorObject: Error = {
      name: 'Locked',
      message: 'The case you are trying to add entry for is locked.'
    };
    pipelineStore.casesLoader.setError(errorObject);
    return;
  }

  router.push({
    query: {
      ...route.query,
      [ENTRY_DIALOG_QUERY.form]: formIdentifier,
      [ENTRY_DIALOG_QUERY.case]: caseItem.identifier,
      [ENTRY_DIALOG_QUERY.entry]: entryIdentifier
    }
  });
};
</script>

<template>
  <div class="pipeline-root">
    <Table :columns="pipelineStore.tableColumns"
      :data-list="pipelineStore.caseList"
      :loading-state="pipelineStore.casesLoader.isLoading"
      :selection="true"
      v-if="showTable"
      v-model:selectedRows="selectedRows"
      :rows="pipelineStore.pageLimit"
      :currentRow="(pipelineStore.currentPage - 1) * pipelineStore.pageLimit"
      :totalRecords="pipelineStore.totalCasesCount"
      @onPageChange="pipelineStore.onPageChange"
      presort="createdAt">
      <template #bodyContent="{ data, col, index }">
        <div class="case-item"
          v-if="col.type === TableColumnType.TEXT_WTIH_ICON"
          @click="onCaseClick(data)">
          <TableIconText :text="data[col.key]" :icon="col.icon" v-if="!data.isFinished"/>
          <TableIconText :text="data[col.key]" :icon="{style: 'fal', icon:'folder-check', color: 'rgb(133, 198, 255)'}" v-if="data.isFinished"/>
          <FontAwesomeIcon :icon="[`fas`, `lock`]"
            v-if="data['lockedAt']"
            class="lock"/>
          <CaseNoteButton :caseModel="data"
            class="note-button"
            :class="{ 'has-notes': (data.caseNoteCount ?? 0) > 0 }"
            v-if="canShowCaseNotes"
            @onPinChange="emit('onNotesPinChange', $event)"/>
          <PerfusionScanButton :caseModel="data"
            class="scan-button"
            v-if="studyStore.isPerfusionCompanionStudy"/>
        </div>

        <PipelineCell :caseModel="data"
          :index="index"
          v-if="col.type === TableColumnType.PIPELINE"
          :formIdentifier="col.identifier!"
          @onCellClick="(formId, entryId) => handleCellClick(data, formId, entryId)"/>
      </template>
      <template #emptyContent>
        <div class="empty-content-container">
          <div class="empty-content">
            <h3 class="no-entries-label">{{ emptyContentMessage }}</h3>
          </div>
        </div>
      </template>
    </Table>
  </div>
</template>

<style scoped lang="scss">
.pipeline-root {
  height: 100%;
}
.case-item {
  display: flex;
  align-items: center;
  min-width: 170px;

  .lock {
    margin-left: 8px;
    font-size: 16px;
  }

  .note-button,
  .scan-button {
    margin-left: auto;
    padding-left: 4px;
    opacity: 0;
    transition: opacity 0.15s ease;

    &.has-notes {
      opacity: 1;
    }
  }

  // Only the leading button claims the free space; the scan button then sits beside it.
  .note-button ~ .scan-button {
    margin-left: 0;
  }
}
.case-item:hover .note-button,
.case-item:hover .scan-button {
  opacity: 1;
}
:deep(.p-datatable-column-header-content) {
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.2em;
  max-height: 40px;
}
:deep(.p-datatable-tbody > tr > td:not(.p-datatable-frozen-column)) {
  padding: unset;
  /* A table cell never shrinks below its row, so this stays the full row height —
     but it counts as a *definite* height, which is what PipelineCell's
     `height: 100%` needs to resolve against. `height: 100%` here would not work:
     the <tr> is auto-height, so the percentage stays indefinite and the cell
     content falls back to its own height. */
  height: 1px;
}
:deep(.p-datatable-scrollable-table > .p-datatable-thead) {
  z-index: 2;
}
:deep(.p-datatable-tbody > tr > .p-datatable-frozen-column) {
  z-index: 1;
}
:deep(.p-datatable-tbody > tr > td.p-datatable-frozen-column:nth-of-type(2)),
:deep(.p-datatable-thead > tr > th.p-datatable-frozen-column:nth-of-type(2)) {
  box-shadow: 1px 0 5px -2px rgba(0, 0, 0, 0.4);
}
.empty-content-container {
  position: absolute;
  left: 0;
  width: 100%;
  height: 80%;;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  .empty-content {
    text-align: center;

    .no-entries-label {
      font-weight: bold;
    }
  }
}
</style>
